import { useForm } from "@tanstack/react-form";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import { graphql, useMutation } from "react-relay";
import { toast } from "sonner";
import { z } from "zod";
import { Heading } from "@/components/heading";
import { Text, TextLink } from "@/components/text";
import { Button } from "@/components/ui/button";
import { Field, FieldError, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import type { esqueciASenha_forgotPasswordMutation } from "./__generated__/esqueciASenha_forgotPasswordMutation.graphql";

export const Route = createFileRoute("/_auth/esqueci-a-senha")({
	component: RouteComponent,
});

function RouteComponent() {
	const router = useRouter();

	const [forgotPassword] =
		useMutation<esqueciASenha_forgotPasswordMutation>(graphql`
	    mutation esqueciASenha_forgotPasswordMutation($email: String!) {
				forgotPassword(username: $email)
			}
	`);

	const form = useForm({
		defaultValues: {
			email: "",
		},
		validators: {
			onChange: z.object({
				email: z.string().email("Email inválido"),
			}),
		},
		onSubmit: async ({ value }) => {
			return forgotPassword({
				variables: {
					email: value.email!,
				},
				onCompleted: (response, errors) => {
					if (errors?.length) {
						toast.error(errors[0].message);
						return;
					}
					if (response.forgotPassword) {
						toast.success(
							"Email de recuperação enviado! Verifique sua caixa de entrada.",
						);
						form.reset();
						setTimeout(() => {
							router.navigate({
								to: "/login",
							});
						}, 2000);
					} else {
						toast.error("Não foi possível enviar o email de recuperação.");
					}
				},
				onError: (err) => {
					if (err.message.includes("not found")) {
						toast.error(
							"Não encontramos uma conta com esse email. Verifique o email digitado.",
						);
					} else {
						toast.error(
							"Não foi possível enviar o email de recuperação. Tente novamente.",
						);
					}
				},
			});
		},
	});

	return (
		<div className="flex justify-center w-full">
			<div className="flex w-full flex-col gap-8 justify-center p-6 max-w-[550px]">
				<div className="flex flex-col gap-1.5">
					<Heading>Recuperar Senha</Heading>

					<Text className="text-left text-muted-foreground">
						Digite seu email abaixo e enviaremos um link para você redefinir sua
						senha.
					</Text>
				</div>

				<form
					className="flex flex-col gap-8"
					onSubmit={(e) => {
						e.preventDefault();
						e.stopPropagation();
						form.handleSubmit();
					}}
				>
					<form.Field name="email">
						{(field) => (
							<Field>
								<FieldLabel htmlFor={field.name}>Email</FieldLabel>
								<Input
									id={field.name}
									name={field.name}
									value={field.state.value}
									onBlur={field.handleBlur}
									onChange={(e) => field.handleChange(e.target.value)}
									type="email"
									placeholder="Digite seu email"
									aria-invalid={
										!!(field.state.meta.isTouched && field.state.meta.errors[0])
									}
								/>

								{field.state.meta.isTouched && field.state.meta.errors[0] ? (
									<FieldError errors={field.state.meta.errors} />
								) : null}
							</Field>
						)}
					</form.Field>

					<form.Subscribe
						selector={(state) => [state.isSubmitting, state.isSubmitSuccessful]}
					>
						{([isSubmitting, isSubmitSuccessful]) => (
							<div className="flex flex-col gap-6">
								<Button
									type="submit"
									color={isSubmitSuccessful ? "emerald" : "primary"}
									disabled={isSubmitting || isSubmitSuccessful}
									className="flex items-center justify-center gap-2"
								>
									Enviar email de recuperação{" "}
									{isSubmitting ? <Spinner /> : null}
								</Button>

								<Text className="font-medium">
									Lembrou sua senha?{" "}
									<TextLink to="/login" disabled={isSubmitting}>
										Voltar para login
									</TextLink>
								</Text>
							</div>
						)}
					</form.Subscribe>
				</form>
			</div>
		</div>
	);
}
